import * as _ from 'lodash-es';
import { BlockEmbed } from 'quill/blots/block';
import { Parchment } from 'quill/core';
import React from 'react';
import { createPortal } from 'react-dom';
import { LINK_TYPE_ATTRIBUTE, LINK_TYPES } from '../constants';
import { getEmbedPresentationOptions } from '../embed/EmbedComponent';
import EmbedContent from '../embed/EmbedContent';
import type { EmbedValue } from '../embed/matchEmbed';
import { EmbedMode, embedUrl } from '../embed/matchEmbed';
import Comment from './Comment';
import Diff from './Diff';
import Height from './Height';
import Reaction from './Reaction';
import Scroll from './Scroll';
import Span from './Span';
import * as styles from './Embed.module.scss';
import VersionChange from './VersionChange';

export const DOCUMENT_LIKE_TYPES = new Set([
  'post',
  'zendesk-article',
  'quip-thread',
  'google-doc',
  'coda',
]);

class Embed extends BlockEmbed {
  static blotName = 'embed';
  static className = 'ql-embed';
  static tagName = 'div';

  static shouldLazyLoad = true;

  static create(value: EmbedValue) {
    const node = super.create(value) as HTMLDivElement;
    node.setAttribute('data-id', value.id);
    node.setAttribute('data-type', value.type);
    if (value.params) {
      node.setAttribute('data-params', value.params);
    }
    // @ts-expect-error
    node.setAttribute('contenteditable', false);

    const isDocumentLike = DOCUMENT_LIKE_TYPES.has(value.type);

    node.classList.add(
      styles.container,
      isDocumentLike ? styles.isDocumentLike : null,
    );

    if (getEmbedPresentationOptions(value.type).renderMode === 'image') {
      node.classList.add('ql-embed-image');
    }
    return node;
  }

  static value(domNode: Element) {
    const value = {
      type: domNode.getAttribute('data-type'),
      id: domNode.getAttribute('data-id'),
    };

    const params = domNode.getAttribute('data-params');
    return params ? { ...value, params } : value;
  }

  id: string;

  constructor(scroll: Parchment.ScrollBlot, domNode: Element) {
    super(scroll, domNode);
    this.id = _.uniqueId();
  }

  attach() {
    super.attach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_MOUNT, this);
  }

  detach() {
    super.detach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_UNMOUNT, this);
  }

  html() {
    const url = embedUrl(this.value().embed, EmbedMode.FULL);
    return url
      ? `<a ${LINK_TYPE_ATTRIBUTE}="${LINK_TYPES.embed}" href=${url}>${url}</a>`
      : '';
  }

  format(name: string, value: unknown) {
    if (
      name === Comment.attrName ||
      name === Reaction.attrName ||
      name === Span.attrName ||
      name === Height.attrName ||
      name === Diff.attrName ||
      name === VersionChange.attrName
    ) {
      const attributor = this.scroll.query(name, Parchment.Scope.ATTRIBUTE);
      if (attributor) {
        // @ts-expect-error
        this.attributes.attribute(attributor, value);
      }
    } else {
      super.format(name, value);
    }
  }

  formats() {
    return this.attributes.values();
  }

  getReferenceNodeForBounds() {
    return this.domNode.querySelector('.embed-content') || this.domNode;
  }

  renderPortal({
    readOnly,
    selected,
  }: {
    readOnly: boolean;
    selected: boolean;
  }) {
    const { id, type, params } = this.value().embed;
    return createPortal(
      <EmbedContent
        id={id}
        type={type}
        params={params}
        readOnly={readOnly}
        disableContentSelection={!selected}
        selected={selected}
      />,
      this.domNode,
    );
  }
}

export default Embed;
